import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Check, Loader2, CreditCard, Banknote, Package, Truck, MapPin, Map, ExternalLink, X } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// InPost Geowidget Token (no domain restriction)
const INPOST_TOKEN = "eyJhbGciOiJSUzI1NiIsInR5cCIgOiAiSldUIiwia2lkIiA6ICJzQlpXVzFNZzVlQnpDYU1XU3JvTlBjRWFveFpXcW9Ua2FuZVB3X291LWxvIn0.eyJleHAiOjIwOTA3ODMwMDUsImlhdCI6MTc3NTQyMzAwNSwianRpIjoiMzNiODdiYWQtZGQ5ZS00ZTQ2LWI2NjQtOGUxMmE3ODM0M2UyIiwiaXNzIjoiaHR0cHM6Ly9sb2dpbi5pbnBvc3QucGwvYXV0aC9yZWFsbXMvZXh0ZXJuYWwiLCJzdWIiOiJmOjEyNDc1MDUxLTFjMDMtNGU1OS1iYTBjLTJiNDU2OTVlZjUzNTpPNnFqVmZWWWVEdVNBX3ZDYkZIb3NPYzk5d3JPTlV6UU5fWEtrbkJQYmU4IiwidHlwIjoiQmVhcmVyIiwiYXpwIjoic2hpcHgiLCJzZXNzaW9uX3N0YXRlIjoiNDRjZjBhMzItNTE5Ny00NzIzLWFiMWMtZGM0Y2FlYTZlODMyIiwic2NvcGUiOiJvcGVuaWQgYXBpOmFwaXBvaW50cyIsInNpZCI6IjQ0Y2YwYTMyLTUxOTctNDcyMy1hYjFjLWRjNGNhZWE2ZTgzMiIsImFsbG93ZWRfcmVmZXJyZXJzIjoiIiwidXVpZCI6IjVkOTJkZTExLTY4OTMtNGNkNS1hOTIwLWNkZTM1ODY5OTQ3NyJ9.o1OzR4k-3WYtjKAYga78oZXv3RcnrRsmWAnXJXgG7Zlgg5Uyx4FmYHJDWgc6kvskE9ivfK3VfvmCf_ThSPTV5IW9EQql9_ANld9bls7amLbiCmNOCo8T2BZ7NNA6zYYMsS7WBZr8h8CF7ztWClz5IoVf_yRLMPmMY1ik2GJGvnKueqRaN6DGjxaJlb55wklvoVJE9wHbThiHrHC-edhBKkxH7zh1klrzwp1AnezFDgoVLzUZkpvP8Wq-XLRtwm3VlLJX5C2ZQ5YXuud4kzvsgTG1E9MLbjssWDUepspQtiDDPrb9NrIm9CzwkM0TlUiJNiRPdqucWrPXZZtNlRUfag";

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, cartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('payu');
  const [deliveryMethod, setDeliveryMethod] = useState('inpost');
  const [showInpostMap, setShowInpostMap] = useState(false);
  const [selectedLocker, setSelectedLocker] = useState(null);
  const [manualLockerInput, setManualLockerInput] = useState('');
  const geowidgetContainerRef = useRef(null);
  const [formData, setFormData] = useState({
    full_name: user?.name || '',
    email: user?.email || '',
    address: '',
    city: '',
    postal_code: '',
    phone: '',
    inpost_locker: ''
  });

  // InPost point selection handler
  const handlePointSelected = useCallback((point) => {
    if (point && point.name) {
      setSelectedLocker(point);
      setFormData(prev => ({ ...prev, inpost_locker: point.name }));
      setShowInpostMap(false);
      toast.success(`Wybrano: ${point.name}`);
    }
  }, []);

  // Initialize InPost Geowidget when modal opens
  useEffect(() => {
    if (!showInpostMap) return;
    
    let widgetRef = null;
    let widgetEventHandler = null;

    // Global handler function for InPost onpoint attribute (this is the recommended method)
    window.handleInpostPointSelect = (point) => {
      console.log('InPost point selected via onpoint attribute:', point);
      if (point && point.name) {
        handlePointSelected(point);
      }
    };

    // Wait for container to be available, then create widget
    const timer = setTimeout(() => {
      const container = document.getElementById('inpost-geowidget-container');
      if (!container) return;

      // Clear container
      container.innerHTML = '';
      
      // Create geowidget element - using onpoint attribute as per InPost docs
      const widget = document.createElement('inpost-geowidget');
      widget.setAttribute('id', 'inpost-geowidget-instance');
      widget.setAttribute('token', INPOST_TOKEN);
      widget.setAttribute('language', 'pl');
      widget.setAttribute('config', 'parcelcollect');
      widget.setAttribute('onpoint', 'handleInpostPointSelect'); // Primary method per docs
      widget.style.width = '100%';
      widget.style.height = '100%';
      widget.style.display = 'block';
      widget.style.minHeight = '500px';
      
      container.appendChild(widget);
      widgetRef = widget;
      
      // Backup: Also listen for onpointselect event on the widget element
      widgetEventHandler = (event) => {
        const point = event.detail;
        console.log('InPost point selected via addEventListener:', point);
        if (point && point.name) {
          handlePointSelected(point);
        }
      };
      widget.addEventListener('onpointselect', widgetEventHandler);
      
      // Also try document-level listener (some InPost implementations fire here)
      document.addEventListener('onpointselect', widgetEventHandler);
    }, 100);
    
    // Cleanup
    return () => {
      clearTimeout(timer);
      if (widgetRef && widgetEventHandler) {
        widgetRef.removeEventListener('onpointselect', widgetEventHandler);
      }
      document.removeEventListener('onpointselect', widgetEventHandler);
      delete window.handleInpostPointSelect;
    };
  }, [showInpostMap, handlePointSelected]);

  // Manual locker input confirmation
  const confirmManualLocker = () => {
    const locker = manualLockerInput.trim().toUpperCase();
    if (locker && locker.length >= 3) {
      setSelectedLocker({ name: locker });
      setFormData(prev => ({ ...prev, inpost_locker: locker }));
      setShowInpostMap(false);
      setManualLockerInput('');
      toast.success(`Wybrano: ${locker}`);
    } else {
      toast.error('Wpisz poprawny numer paczkomatu');
    }
  };

  const deliveryPrices = {
    inpost: 12.99,      // InPost Paczkomat
    dpd: 19.99,         // DPD Kurier
    dpd_pickup: 14.99   // DPD Punkt odbioru
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(price);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const deliveryCost = cartTotal >= 500 ? 0 : deliveryPrices[deliveryMethod];
  const finalTotal = cartTotal + deliveryCost;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (cartItems.length === 0) {
      toast.error(t.cart.empty);
      navigate('/shop');
      return;
    }

    // Validate email for guest checkout
    if (!formData.email) {
      toast.error(t.checkout?.emailRequired || 'Email jest wymagany');
      return;
    }

    // Validate InPost locker if selected
    if (deliveryMethod === 'inpost' && !formData.inpost_locker) {
      toast.error(t.checkout?.inpostLockerRequired || 'Podaj numer paczkomatu');
      return;
    }

    setLoading(true);

    try {
      // Create order - use guest endpoint if not logged in
      const orderData = {
        items: cartItems.map(item => ({
          product_id: item.product_id || item.product?.id,
          quantity: item.quantity
        })),
        shipping_info: {
          full_name: formData.full_name,
          address: formData.address,
          city: formData.city,
          postal_code: formData.postal_code,
          phone: formData.phone,
          delivery_method: deliveryMethod,
          delivery_cost: deliveryCost,
          inpost_locker: deliveryMethod === 'inpost' ? formData.inpost_locker : null
        },
        email: formData.email,
        language: language // Pass current language for email
      };

      // Use guest endpoint if not logged in, regular endpoint if logged in
      const orderEndpoint = user ? `${API}/orders` : `${API}/orders/guest`;
      const orderResponse = await axios.post(orderEndpoint, orderData);
      const orderId = orderResponse.data.id;

      if (paymentMethod === 'payu') {
        // Create PayU payment
        const paymentData = {
          order_id: orderId,
          amount: finalTotal,
          description: `Zamówienie #${orderId.slice(0, 8)}`,
          buyer_email: formData.email,
          buyer_first_name: formData.full_name.split(' ')[0] || '',
          buyer_last_name: formData.full_name.split(' ').slice(1).join(' ') || '',
          buyer_phone: formData.phone
        };

        const paymentResponse = await axios.post(`${API}/payu/create-payment`, paymentData);
        
        if (paymentResponse.data.success && paymentResponse.data.redirect_url) {
          clearCart();
          // Redirect to PayU payment page
          window.location.href = paymentResponse.data.redirect_url;
          return;
        } else {
          throw new Error('Payment creation failed');
        }
      } else {
        // Cash on delivery
        clearCart();
        toast.success(t.checkout.orderConfirmed);
        navigate(`/checkout/success?order_id=${orderId}`);
      }
    } catch (error) {
      console.error('Order/Payment failed:', error);
      toast.error(error.response?.data?.detail || t.common.error);
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen" data-testid="checkout-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Link to="/cart" className="inline-flex items-center gap-2 font-mono text-sm tracking-widest uppercase text-zinc-400 hover:text-white transition-colors mb-4">
            <ArrowLeft size={16} /> {t.checkout.backToCart}
          </Link>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase leading-none text-white" data-testid="checkout-title">
            {t.checkout.title}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="border border-zinc-800 bg-[#111111] p-6 mb-6">
              <h2 className="font-heading text-2xl uppercase text-white mb-6">{t.checkout.shippingInfo}</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">{t.checkout.fullName}</label>
                  <input type="text" name="full_name" value={formData.full_name} onChange={handleChange} required className="bg-[#050505] border border-zinc-700 text-white px-4 py-3 font-sans focus:border-yellow-400 focus:outline-none focus:ring-1 focus:ring-yellow-400 transition-all w-full" data-testid="shipping-name" />
                </div>

                <div>
                  <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required className="bg-[#050505] border border-zinc-700 text-white px-4 py-3 font-sans focus:border-yellow-400 focus:outline-none focus:ring-1 focus:ring-yellow-400 transition-all w-full" data-testid="shipping-email" />
                </div>

                <div>
                  <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">{t.checkout.streetAddress}</label>
                  <input type="text" name="address" value={formData.address} onChange={handleChange} required className="bg-[#050505] border border-zinc-700 text-white px-4 py-3 font-sans focus:border-yellow-400 focus:outline-none focus:ring-1 focus:ring-yellow-400 transition-all w-full" data-testid="shipping-address" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">{t.checkout.city}</label>
                    <input type="text" name="city" value={formData.city} onChange={handleChange} required className="bg-[#050505] border border-zinc-700 text-white px-4 py-3 font-sans focus:border-yellow-400 focus:outline-none focus:ring-1 focus:ring-yellow-400 transition-all w-full" data-testid="shipping-city" />
                  </div>
                  <div>
                    <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">{t.checkout.postalCode}</label>
                    <input type="text" name="postal_code" value={formData.postal_code} onChange={handleChange} required className="bg-[#050505] border border-zinc-700 text-white px-4 py-3 font-sans focus:border-yellow-400 focus:outline-none focus:ring-1 focus:ring-yellow-400 transition-all w-full" data-testid="shipping-postal" />
                  </div>
                </div>

                <div>
                  <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">{t.checkout.phone}</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="bg-[#050505] border border-zinc-700 text-white px-4 py-3 font-sans focus:border-yellow-400 focus:outline-none focus:ring-1 focus:ring-yellow-400 transition-all w-full" data-testid="shipping-phone" />
                </div>

                {/* Delivery Method */}
                <div className="border-t border-zinc-700 pt-6">
                  <h3 className="font-heading text-xl uppercase text-white mb-4">{t.checkout?.deliveryMethod || 'Sposób dostawy'}</h3>
                  
                  <div className="space-y-3">
                    {/* InPost Paczkomat */}
                    <label className={`flex items-center gap-4 p-4 border cursor-pointer transition-colors ${deliveryMethod === 'inpost' ? 'border-yellow-400 bg-yellow-400/10' : 'border-zinc-700 hover:border-zinc-500'}`}>
                      <input type="radio" name="delivery" value="inpost" checked={deliveryMethod === 'inpost'} onChange={() => setDeliveryMethod('inpost')} className="sr-only" />
                      <div className={`w-5 h-5 border-2 rounded-full flex items-center justify-center ${deliveryMethod === 'inpost' ? 'border-yellow-400' : 'border-zinc-500'}`}>
                        {deliveryMethod === 'inpost' && <div className="w-2.5 h-2.5 bg-yellow-400 rounded-full" />}
                      </div>
                      <Package size={24} className={deliveryMethod === 'inpost' ? 'text-yellow-400' : 'text-zinc-400'} />
                      <div className="flex-1">
                        <p className={`font-bold ${deliveryMethod === 'inpost' ? 'text-white' : 'text-zinc-300'}`}>InPost Paczkomat</p>
                        <p className="text-zinc-500 text-sm">{t.checkout?.inpostDesc || '24/7, odbiór w 48h'}</p>
                      </div>
                      <span className={`font-mono ${deliveryMethod === 'inpost' ? 'text-yellow-400' : 'text-zinc-400'}`}>
                        {cartTotal >= 500 ? t.cart?.free || 'GRATIS' : formatPrice(12.99)}
                      </span>
                    </label>

                    {/* DPD Kurier */}
                    <label className={`flex items-center gap-4 p-4 border cursor-pointer transition-colors ${deliveryMethod === 'dpd' ? 'border-yellow-400 bg-yellow-400/10' : 'border-zinc-700 hover:border-zinc-500'}`}>
                      <input type="radio" name="delivery" value="dpd" checked={deliveryMethod === 'dpd'} onChange={() => setDeliveryMethod('dpd')} className="sr-only" />
                      <div className={`w-5 h-5 border-2 rounded-full flex items-center justify-center ${deliveryMethod === 'dpd' ? 'border-yellow-400' : 'border-zinc-500'}`}>
                        {deliveryMethod === 'dpd' && <div className="w-2.5 h-2.5 bg-yellow-400 rounded-full" />}
                      </div>
                      <Truck size={24} className={deliveryMethod === 'dpd' ? 'text-yellow-400' : 'text-zinc-400'} />
                      <div className="flex-1">
                        <p className={`font-bold ${deliveryMethod === 'dpd' ? 'text-white' : 'text-zinc-300'}`}>DPD Kurier</p>
                        <p className="text-zinc-500 text-sm">{t.checkout?.dpdDesc || 'Dostawa pod drzwi, 1-2 dni'}</p>
                      </div>
                      <span className={`font-mono ${deliveryMethod === 'dpd' ? 'text-yellow-400' : 'text-zinc-400'}`}>
                        {cartTotal >= 500 ? t.cart?.free || 'GRATIS' : formatPrice(19.99)}
                      </span>
                    </label>

                    {/* DPD Pickup */}
                    <label className={`flex items-center gap-4 p-4 border cursor-pointer transition-colors ${deliveryMethod === 'dpd_pickup' ? 'border-yellow-400 bg-yellow-400/10' : 'border-zinc-700 hover:border-zinc-500'}`}>
                      <input type="radio" name="delivery" value="dpd_pickup" checked={deliveryMethod === 'dpd_pickup'} onChange={() => setDeliveryMethod('dpd_pickup')} className="sr-only" />
                      <div className={`w-5 h-5 border-2 rounded-full flex items-center justify-center ${deliveryMethod === 'dpd_pickup' ? 'border-yellow-400' : 'border-zinc-500'}`}>
                        {deliveryMethod === 'dpd_pickup' && <div className="w-2.5 h-2.5 bg-yellow-400 rounded-full" />}
                      </div>
                      <MapPin size={24} className={deliveryMethod === 'dpd_pickup' ? 'text-yellow-400' : 'text-zinc-400'} />
                      <div className="flex-1">
                        <p className={`font-bold ${deliveryMethod === 'dpd_pickup' ? 'text-white' : 'text-zinc-300'}`}>DPD Pickup</p>
                        <p className="text-zinc-500 text-sm">{t.checkout?.dpdPickupDesc || 'Punkt odbioru DPD'}</p>
                      </div>
                      <span className={`font-mono ${deliveryMethod === 'dpd_pickup' ? 'text-yellow-400' : 'text-zinc-400'}`}>
                        {cartTotal >= 500 ? t.cart?.free || 'GRATIS' : formatPrice(14.99)}
                      </span>
                    </label>
                  </div>

                  {/* InPost Locker Selection */}
                  {deliveryMethod === 'inpost' && (
                    <div className="mt-4">
                      {selectedLocker ? (
                        <div className="bg-[#0a0a0a] border border-yellow-400/50 p-4 rounded mb-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-bold text-yellow-400 text-lg">{selectedLocker.name}</p>
                              <p className="text-zinc-400 text-sm mt-1">
                                {selectedLocker.address?.line1 || selectedLocker.address_details?.street || ''}
                              </p>
                              <p className="text-zinc-400 text-sm">
                                {selectedLocker.address?.zip || selectedLocker.address_details?.post_code || ''} {selectedLocker.address?.city || selectedLocker.address_details?.city || ''}
                              </p>
                            </div>
                            <button 
                              type="button"
                              onClick={() => {
                                setSelectedLocker(null);
                                setFormData(prev => ({ ...prev, inpost_locker: '' }));
                              }}
                              className="text-yellow-400 hover:text-yellow-300 text-sm font-mono uppercase"
                            >
                              Zmień
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button 
                          type="button"
                          onClick={() => setShowInpostMap(true)}
                          className="w-full flex items-center justify-center gap-3 bg-yellow-400 hover:bg-yellow-300 text-black font-bold py-4 px-6 transition-colors mb-3"
                          data-testid="select-inpost-btn"
                        >
                          <Map size={20} />
                          Wybierz Paczkomat na mapie
                        </button>
                      )}
                      <input type="hidden" name="inpost_locker" value={formData.inpost_locker} />
                    </div>
                  )}
                </div>

                {/* Payment Method */}
                <div className="border-t border-zinc-700 pt-6">
                  <h3 className="font-heading text-xl uppercase text-white mb-4">{t.checkout?.paymentMethod || 'Metoda płatności'}</h3>
                  
                  <div className="space-y-3">
                    <label className={`flex items-center gap-4 p-4 border cursor-pointer transition-colors ${paymentMethod === 'payu' ? 'border-yellow-400 bg-yellow-400/10' : 'border-zinc-700 hover:border-zinc-500'}`}>
                      <input 
                        type="radio" 
                        name="payment" 
                        value="payu" 
                        checked={paymentMethod === 'payu'} 
                        onChange={() => setPaymentMethod('payu')}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 border-2 rounded-full flex items-center justify-center ${paymentMethod === 'payu' ? 'border-yellow-400' : 'border-zinc-500'}`}>
                        {paymentMethod === 'payu' && <div className="w-2.5 h-2.5 bg-yellow-400 rounded-full" />}
                      </div>
                      <CreditCard size={24} className={paymentMethod === 'payu' ? 'text-yellow-400' : 'text-zinc-400'} />
                      <div className="flex-1">
                        <p className={`font-bold ${paymentMethod === 'payu' ? 'text-white' : 'text-zinc-300'}`}>PayU</p>
                        <p className="text-zinc-500 text-sm">BLIK, karta, przelew bankowy</p>
                      </div>
                    </label>

                    <label className={`flex items-center gap-4 p-4 border cursor-pointer transition-colors ${paymentMethod === 'cash' ? 'border-yellow-400 bg-yellow-400/10' : 'border-zinc-700 hover:border-zinc-500'}`}>
                      <input 
                        type="radio" 
                        name="payment" 
                        value="cash" 
                        checked={paymentMethod === 'cash'} 
                        onChange={() => setPaymentMethod('cash')}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 border-2 rounded-full flex items-center justify-center ${paymentMethod === 'cash' ? 'border-yellow-400' : 'border-zinc-500'}`}>
                        {paymentMethod === 'cash' && <div className="w-2.5 h-2.5 bg-yellow-400 rounded-full" />}
                      </div>
                      <Banknote size={24} className={paymentMethod === 'cash' ? 'text-yellow-400' : 'text-zinc-400'} />
                      <div className="flex-1">
                        <p className={`font-bold ${paymentMethod === 'cash' ? 'text-white' : 'text-zinc-300'}`}>{t.checkout?.cashOnDelivery || 'Za pobraniem'}</p>
                        <p className="text-zinc-500 text-sm">{t.checkout?.payOnDelivery || 'Płatność przy odbiorze'}</p>
                      </div>
                    </label>
                  </div>
                </div>

                <button type="submit" disabled={loading} className="w-full bg-yellow-400 text-black uppercase font-bold tracking-widest px-8 py-4 border-2 border-yellow-400 hover:bg-black hover:text-yellow-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="place-order-btn">
                  {loading ? (
                    <><Loader2 size={20} className="animate-spin" /> {t.checkout.processing}</>
                  ) : paymentMethod === 'payu' ? (
                    <><CreditCard size={20} /> {t.checkout?.payNow || 'Zapłać teraz'}</>
                  ) : (
                    t.checkout.placeOrder
                  )}
                </button>
              </form>
            </div>
          </div>

          <div>
            <div className="border border-zinc-800 bg-[#111111] p-6 sticky top-24">
              <h2 className="font-heading text-2xl uppercase text-white mb-6">{t.cart.orderSummary}</h2>

              <div className="space-y-4 mb-6">
                {cartItems.map((item) => (
                  <div key={item.product_id || item.product?.id} className="flex gap-4">
                    <div className="w-16 h-16 bg-zinc-800 flex-shrink-0">
                      {item.product?.image_url && (
                        <img src={item.product?.image_url} alt={item.product?.name} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-white text-sm">{item.product?.name}</h4>
                      <p className="text-zinc-500 text-sm">{t.common.qty}: {item.quantity}</p>
                    </div>
                    <span className="font-mono text-yellow-500">{formatPrice((item.product?.price || 0) * item.quantity)}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-zinc-700 pt-4 space-y-2">
                <div className="flex justify-between text-zinc-400">
                  <span>{t.cart.subtotal}</span>
                  <span className="font-mono">{formatPrice(cartTotal)}</span>
                </div>
                <div className="flex justify-between text-zinc-400">
                  <span>
                    {t.cart.shipping} 
                    <span className="text-xs ml-1">
                      ({deliveryMethod === 'inpost' ? 'InPost' : deliveryMethod === 'dpd' ? 'DPD Kurier' : 'DPD Pickup'})
                    </span>
                  </span>
                  <span className="font-mono">{cartTotal >= 500 ? t.cart.free : formatPrice(deliveryCost)}</span>
                </div>
                <div className="flex justify-between text-white text-lg pt-2 border-t border-zinc-700 mt-2">
                  <span className="font-bold">{t.cart.total}</span>
                  <span className="font-mono text-yellow-500" data-testid="checkout-total">{formatPrice(finalTotal)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* InPost Geowidget Modal */}
      {showInpostMap && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/90" data-testid="inpost-modal">
          <div className="bg-[#111111] border border-zinc-700 w-full max-w-5xl h-[90vh] flex flex-col rounded-lg overflow-hidden">
            <div className="flex items-center justify-between p-3 sm:p-4 border-b border-zinc-700 bg-[#0a0a0a]">
              <h3 className="font-heading text-lg sm:text-xl text-white uppercase">Wybierz Paczkomat</h3>
              <button 
                type="button"
                onClick={() => setShowInpostMap(false)}
                className="text-zinc-400 hover:text-white p-1"
              >
                <X size={24} />
              </button>
            </div>
            <div 
              id="inpost-geowidget-container"
              className="flex-1 relative"
              style={{ width: '100%', height: '100%', minHeight: '500px' }}
            />
            <div className="p-3 sm:p-4 border-t border-zinc-700 bg-[#0a0a0a]">
              <p className="text-zinc-400 text-xs sm:text-sm text-center mb-3">
                Wybierz paczkomat na mapie i skopiuj jego numer, następnie wpisz go poniżej
              </p>
              <div className="flex gap-2 sm:gap-3">
                <input 
                  type="text" 
                  placeholder="np. WAW123M"
                  className="flex-1 bg-[#050505] border border-zinc-700 text-white px-3 sm:px-4 py-3 focus:border-yellow-400 focus:outline-none text-sm sm:text-base"
                  onChange={(e) => setFormData(prev => ({ ...prev, inpost_locker: e.target.value.toUpperCase() }))}
                  value={formData.inpost_locker}
                />
                <button 
                  type="button"
                  onClick={() => {
                    if (formData.inpost_locker && formData.inpost_locker.length >= 3) {
                      setSelectedLocker({ name: formData.inpost_locker });
                      setShowInpostMap(false);
                      toast.success(`Wybrano: ${formData.inpost_locker}`);
                    } else {
                      toast.error('Wpisz poprawny numer paczkomatu');
                    }
                  }}
                  className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-4 sm:px-6 py-3 text-sm sm:text-base"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
